import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';

// Web port of app/src/lib/talking.ts. Per-group Realtime Broadcast channel
// for "who is talking right now". No DB writes, pure ephemeral signaling.
// Publisher fires on press-in (on: true) and release (on: false); receivers
// maintain a Set<user_id> rendered as animated bars next to each talking
// crew member. Auto-expires each entry at 15s in case a sender crashes
// mid-press and never broadcasts the release.

type TalkingEvent = {
  user_id: string;
  on: boolean;
};

const TOPIC_PREFIX = 'group-talking:';
const AUTO_EXPIRE_MS = 15_000;

export function talkingTopic(groupId: string) {
  return TOPIC_PREFIX + groupId;
}

export function useTalkingPublisher(groupId: string | null, selfId: string) {
  const chanRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!groupId) return;
    const chan = supabase.channel(talkingTopic(groupId), {
      config: { broadcast: { self: false } },
    });
    chan.subscribe();
    chanRef.current = chan;
    return () => {
      // Fire a final "off" in case we're mid-press during unmount.
      chan
        .send({ type: 'broadcast', event: 'talking', payload: { user_id: selfId, on: false } })
        .catch(() => {});
      supabase.removeChannel(chan);
      chanRef.current = null;
    };
  }, [groupId, selfId]);

  return {
    signal(on: boolean) {
      const c = chanRef.current;
      if (!c) return;
      c.send({
        type: 'broadcast',
        event: 'talking',
        payload: { user_id: selfId, on } satisfies TalkingEvent,
      }).catch(() => {});
    },
  };
}

export function useGroupTalking(groupId: string | null): Set<string> {
  const [talking, setTalking] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    if (!groupId) {
      setTalking(new Set());
      return;
    }
    const chan = supabase.channel(talkingTopic(groupId), {
      config: { broadcast: { self: false } },
    });
    chan.on('broadcast', { event: 'talking' }, ({ payload }) => {
      const evt = payload as TalkingEvent;
      if (!evt?.user_id) return;
      setTalking((prev) => {
        const next = new Set(prev);
        if (evt.on) next.add(evt.user_id);
        else next.delete(evt.user_id);
        return next;
      });
      const existing = timersRef.current.get(evt.user_id);
      if (existing) clearTimeout(existing);
      if (evt.on) {
        const t = setTimeout(() => {
          setTalking((prev) => {
            if (!prev.has(evt.user_id)) return prev;
            const next = new Set(prev);
            next.delete(evt.user_id);
            return next;
          });
          timersRef.current.delete(evt.user_id);
        }, AUTO_EXPIRE_MS);
        timersRef.current.set(evt.user_id, t);
      } else {
        timersRef.current.delete(evt.user_id);
      }
    });
    chan.subscribe();
    return () => {
      supabase.removeChannel(chan);
    };
  }, [groupId]);

  return talking;
}
